import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Table, Tabs } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import Main from 'components/layout/Main';
import { AppDispatch, CombinedStates } from 'core/types';
import { exportAdHocBookings, fetchAdHocBookings } from 'core/dailyReports/dailyReportsApi';
import { AdHocRow } from 'core/dailyReports/types';
import { buildMainToolbar } from 'core/utils/mainToolbar';
import DailyReportDateBar, { DateRange } from 'pages/Dashboard/components/DailyReportDateBar';
import ScheduleModal from 'pages/VisitorRequestView/components/ScheduleModal';
import { useInlineSchedule } from './useInlineSchedule';
import 'pages/Dashboard/components/dailyReportModal.scss';

const AdHocSection: React.FC<{ title: string; rows: AdHocRow[]; loading: boolean; columns: ColumnsType<AdHocRow> }> = ({ title, rows, loading, columns }) => (
    <div className="daily-report-modal__section">
        <h3 className="daily-report-modal__section-title">{title}</h3>
        <Table<AdHocRow>
            rowKey={(row, index) => `${row.requestId}-${index}`}
            columns={columns}
            dataSource={rows}
            loading={loading}
            pagination={false}
            size="small"
            scroll={{ x: true }}
            locale={{ emptyText: `No ${title.toLowerCase()} bookings for this date.` }} />
    </div>
);

const DISPLAY_DATE_FORMAT = 'DD/MM/YYYY';

// A range in the URL as from/to; absent means the single `date` day.
const rangeFromParams = (from: string | null, to: string | null): DateRange | null => (
    from && to ? [dayjs(from).startOf('day'), dayjs(to).startOf('day')] : null
);

// Nothing left to schedule: the shift has been completed, or its day has passed.
// Judged per row on the row's own date, so a range covering past and future days
// only locks the past ones.
const isRowLocked = (row: AdHocRow): boolean => (
    row.isCompleted || (!!row.date && dayjs(row.date).startOf('day').isBefore(dayjs().startOf('day')))
);

const AdHocBookings: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { userData } = useSelector((state: CombinedStates) => state.user);
    const {
        adHocAm, adHocPm, retailAdHocAm, retailAdHocPm, isFetchingAdHoc, isExporting, error,
    } = useSelector((state: CombinedStates) => state.dailyReports);

    const dateParam = searchParams.get('date');
    const [date, setDate] = useState<Dayjs>(dateParam ? dayjs(dateParam).startOf('day') : dayjs().startOf('day'));
    const [range, setRange] = useState<DateRange | null>(
        rangeFromParams(searchParams.get('from'), searchParams.get('to')),
    );
    const [activeTab, setActiveTab] = useState<'adhoc' | 'retail'>('adhoc');

    // A range supersedes the single date; without one, the range is that one day.
    const from = range ? range[0] : date;
    const to = range ? range[1] : date;

    // Stable across renders (only changes with the dates/dispatch) so the effect
    // below and the scheduling hook don't see a new identity every render.
    const refresh = useCallback((): void => {
        dispatch(fetchAdHocBookings({
            date: (range ? range[0] : date).format('YYYY-MM-DD'),
            endDate: range ? range[1].format('YYYY-MM-DD') : undefined,
        }));
    }, [dispatch, date, range]);

    // Inline officer scheduling (assign without leaving this list).
    const {
        scheduleRow,
        shiftGroups,
        securityTeam,
        isRetail: isRetailSchedule,
        loading: isSchedulingBusy,
        openSchedule,
        closeSchedule,
        handleScheduleComplete,
    } = useInlineSchedule<AdHocRow>(refresh);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Keep the dates in the URL so the Back link from scheduling returns here on
    // the same day (or range).
    const syncUrl = (value: Dayjs, nextRange: DateRange | null): void => {
        setSearchParams(
            nextRange
                ? {
                    date: value.format('YYYY-MM-DD'),
                    from: nextRange[0].format('YYYY-MM-DD'),
                    to: nextRange[1].format('YYYY-MM-DD'),
                }
                : { date: value.format('YYYY-MM-DD') },
            { replace: true },
        );
    };

    const handleDateChange = (value: Dayjs): void => {
        setDate(value);
        syncUrl(value, range);
    };

    const handleRangeChange = (value: DateRange | null): void => {
        setRange(value);
        syncUrl(date, value);
    };

    // Open the request's review/schedule screen, remembering to return to this
    // list on the same dates.
    const openRequest = (row: AdHocRow): void => {
        if (!row.encryptedId) {
            return;
        }
        const params = range
            ? `date=${date.format('YYYY-MM-DD')}&from=${from.format('YYYY-MM-DD')}&to=${to.format('YYYY-MM-DD')}`
            : `date=${date.format('YYYY-MM-DD')}`;
        navigate(`/visitor-view-request/${row.encryptedId}`, { state: { returnTo: `/ad-hoc-bookings?${params}` } });
    };

    // Retail stores are identified by their store code, so it sits with the
    // name. Only retail sites have one.
    const siteLabel = (row: AdHocRow): string | null => (
        row.siteStoreCode ? `${row.siteName} (${row.siteStoreCode})` : row.siteName
    );

    const columns: ColumnsType<AdHocRow> = [
        {
            title: 'ID',
            dataIndex: 'requestId',
            key: 'requestId',
            width: 70,
            render: (value: number, row: AdHocRow) => (
                row.encryptedId
                    ? <Button type="link" size="small" className="daily-report-modal__id-link" onClick={() => openRequest(row)}>{value}</Button>
                    : value
            ),
        },
        {
            title: 'Site',
            dataIndex: 'siteName',
            key: 'siteName',
            render: (_value: string | null, row: AdHocRow) => siteLabel(row),
        },
        { title: 'Static #', dataIndex: 'siteStaticCode', key: 'siteStaticCode' },
        // Which day the shift falls on. Only worth a column when the list spans
        // more than one day; otherwise the date bar already says.
        ...(range ? [{
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (value: string | null) => (value ? dayjs(value).format(DISPLAY_DATE_FORMAT) : '-'),
        }] : []),
        { title: 'From', dataIndex: 'startDateTime', key: 'startDateTime' },
        { title: 'To', dataIndex: 'endDateTime', key: 'endDateTime' },
        {
            title: 'S/O Name',
            dataIndex: 'securityOfficerName',
            key: 'securityOfficerName',
            // Clickable (red) so the admin can schedule/assign the officer inline.
            // A shift that is done with has nothing left to schedule, so it stays
            // plain text.
            render: (value: string | null, row: AdHocRow) => (
                row.encryptedId && !isRowLocked(row)
                    ? (
                        <Button
                            type="link"
                            size="small"
                            className="daily-report-modal__id-link"
                            onClick={() => openSchedule(row)}>
                            {value || 'TBC'}
                        </Button>
                    )
                    : (value || 'TBC')
            ),
        },
        { title: 'Requester', dataIndex: 'requesterName', key: 'requesterName' },
    ];

    const toolbar = buildMainToolbar(userData?.role?.accessLevel);

    return (
        <Main
            className="login"
            title={<div>Ad Hoc Bookings</div>}
            subHeaderTitle="Ad Hoc Bookings"
            subHeaderHasBackLink
            subHeaderBackLinkCallback={() => navigate('/dashboard')}
            menuWidth={350}
            menuPlacement="right"
            toolbar={toolbar}
            floatingHeader={false}
            footerContent=""
            floatingFooter={false}
            showFooter={false}>
            <div className="main-container dashboard">
                <div className="daily-report-page">
                    <DailyReportDateBar
                        date={date}
                        onChange={handleDateChange}
                        showDateArrows
                        range={range}
                        onRangeChange={handleRangeChange}
                        onExport={() => dispatch(exportAdHocBookings({
                            date: from.format('YYYY-MM-DD'),
                            endDate: range ? to.format('YYYY-MM-DD') : undefined,
                            retail: activeTab === 'retail',
                        }))}
                        isExporting={isExporting}
                        exportDisabled={activeTab === 'retail'
                            ? retailAdHocAm.length === 0 && retailAdHocPm.length === 0
                            : adHocAm.length === 0 && adHocPm.length === 0} />

                    {error && !isFetchingAdHoc ? (
                        <Alert
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="Couldn't load the ad hoc bookings. Please try again or pick another date." />
                    ) : null}

                    <Tabs
                        className="daily-report-tabs"
                        activeKey={activeTab}
                        onChange={(key) => setActiveTab(key as 'adhoc' | 'retail')}
                        items={[
                            {
                                key: 'adhoc',
                                label: 'Ad Hoc Bookings',
                                children: (
                                    <>
                                        <AdHocSection title="AM" rows={adHocAm} loading={isFetchingAdHoc} columns={columns} />
                                        <AdHocSection title="PM" rows={adHocPm} loading={isFetchingAdHoc} columns={columns} />
                                    </>
                                ),
                            },
                            {
                                key: 'retail',
                                label: 'Retail Guarding',
                                children: (
                                    <>
                                        <AdHocSection title="AM" rows={retailAdHocAm} loading={isFetchingAdHoc} columns={columns} />
                                        <AdHocSection title="PM" rows={retailAdHocPm} loading={isFetchingAdHoc} columns={columns} />
                                    </>
                                ),
                            },
                        ]} />
                </div>
            </div>
            <ScheduleModal
                shiftData={shiftGroups}
                isModalOpen={scheduleRow !== null}
                handleCancel={closeSchedule}
                handleMultiShiftComplete={handleScheduleComplete}
                loading={isSchedulingBusy}
                isRetail={isRetailSchedule}
                securityTeam={securityTeam}
                requestId={scheduleRow?.requestId ?? null}
                siteName={scheduleRow ? siteLabel(scheduleRow) : null} />
        </Main>
    );
};

export default AdHocBookings;
